'use strict';

/**
 * ContractAdmin-Template — the canonical, HNTB-free definition of the "Contract Administration" starter
 * template: the two report forms (Payment, Change Order), the two default approval workflows, and the
 * bindings that tie each form to its workflow.
 *
 * This is the version-controlled SOURCE the Generate action materializes onto the HeadLight customer
 * (IDCustomer 1); the HeadLight copy is then the runtime single source of truth that the per-customer
 * "Set up Contract Administration" action clones into other customers. Everything here is a plain data
 * factory (no pict/REST), so it is reusable by the generate + clone provider and unit-testable.
 *
 * Identifier scheme — per-customer so each tenant owns its own rows/blobs and nothing collides globally:
 *   - Payment RNI:      Hash `HeadLight-Payment-Transitional-<id>`, GUID `HL-CA-RNI-PAYMENT-<id>`
 *   - Change Order RNI: Hash `Default-ChangeOrder-<id>`,            GUID `HL-CA-RNI-CHANGEORDER-<id>`
 *   - Workflows:        Configuration ns ApprovalWorkflow, hashes 'Default Payment Approval' /
 *                       'Default Change Order Approval' (names are the Config NamespaceValue).
 *   - Bindings:         ApprovalWorkflowBindings 'Document' map { <formHash>: <workflowName> }.
 *
 * The TidingsData.Type on the payment form points at the GLOBAL server-side Tidings report template
 * 'HeadLight-Payment-Transitional' (NOT suffixed) — that is a shared renderer, not a per-customer row.
 */

const libChangeOrderManifest = require('../change-orders/Default-ChangeOrder-Manifest.json');
const libApprovalModel = require('../approval-workflow/ApprovalWorkflow-Model.js');

const HEADLIGHT_IDCUSTOMER = 1;

// The platform module-catalog row for Contract Administration. ModuleHash is a readable identifier (like the
// other catalog rows: HEADLIGHT_FIELDBOOK, HEADLIGHT_TRACKER, …); enablement for a customer is a CustomerModuleJoin
// keyed by this hash. Distinct from the vendor-specific 'Contracts' (IDOT_CONTRACT_MGMT) row.
const MODULE_HASH = 'HEADLIGHT_CONTRACTADMIN';
const MODULE_NAME = 'Contract Administration';
const MODULE_ORDINAL = 500;

// The RolePermission capability that actually gates the Contract-Admin screens (canModule('ContractAdmin')).
const MODULE_ACCESS_CAPABILITY = 'Module-ContractAdmin-Access';

const PAYMENT_WORKFLOW = 'Default Payment Approval';
const CHANGEORDER_WORKFLOW = 'Default Change Order Approval';

const paymentHash = (pIDCustomer) => `HeadLight-Payment-Transitional-${pIDCustomer}`;
const changeOrderHash = (pIDCustomer) => `Default-ChangeOrder-${pIDCustomer}`;
const paymentGUID = (pIDCustomer) => `HL-CA-RNI-PAYMENT-${pIDCustomer}`;
const changeOrderGUID = (pIDCustomer) => `HL-CA-RNI-CHANGEORDER-${pIDCustomer}`;

/** The Payment report form (ReportNamedInstance) — a thin pointer to the global 'HeadLight-Payment-Transitional' Tidings template. */
function paymentFormRNI(pIDCustomer)
{
	const tmpID = Number(pIDCustomer);
	return {
		GUIDReportNamedInstance: paymentGUID(tmpID),
		IDCustomer: tmpID,
		Name: 'HeadLight Payment',
		Hash: paymentHash(tmpID),
		OptionsJSON: JSON.stringify({
			DefaultEmailFormat: 'pdf',
			TidingsData: { Type: 'HeadLight-Payment-Transitional', Renderer: 'html' },
			FormOnly: true,
			Meta: { reportTitle: '', hideDuplicateButton: true, hideCloningFeature: false },
			Body: { displayOrder: [] },
		}),
		IDReportArchetype: 2,
		DefaultReport: 1,
		Ordinal: 0,
	};
}

/** The Change Order report form (ReportNamedInstance) — an inline HeadlightDynamicForm using the canonical manifest. */
function changeOrderFormRNI(pIDCustomer)
{
	const tmpID = Number(pIDCustomer);
	return {
		GUIDReportNamedInstance: changeOrderGUID(tmpID),
		IDCustomer: tmpID,
		Name: 'Change Order',
		Hash: changeOrderHash(tmpID),
		OptionsJSON: JSON.stringify({
			DefaultEmailFormat: 'pdf',
			TidingsData: { Type: 'headlight-dynamic-form', Renderer: 'html' },
			FormOnly: true,
			Meta: { reportTitle: '' },
			FormDefinition: { Product: 'HeadlightDynamicForm', DefaultFormManifest: libChangeOrderManifest },
		}),
		IDReportArchetype: 0,
		DefaultReport: 1,
		Ordinal: 0,
	};
}

/** A fresh deep copy of the platform-default approval workflow (generic Draft→Submitted→{Approved,Rejected} with one IsApproval edge, keyword 'Admin' actor so it clones verbatim into any customer). */
function defaultWorkflowBlob()
{
	return JSON.parse(JSON.stringify(libApprovalModel.NEW_DEFAULT_WORKFLOW_CONFIG));
}

/**
 * Build the full template artifact set for a customer.
 * @param {number} pIDCustomer
 * @returns {{ IDCustomer:number, ReportForms:Array<Object>, Workflows:Array<{Hash:string,Blob:Object}>, Bindings:Object }}
 */
function buildTemplate(pIDCustomer)
{
	const tmpID = Number(pIDCustomer);
	return {
		IDCustomer: tmpID,
		ReportForms: [ paymentFormRNI(tmpID), changeOrderFormRNI(tmpID) ],
		Workflows:
		[
			{ Hash: PAYMENT_WORKFLOW, Blob: defaultWorkflowBlob() },
			{ Hash: CHANGEORDER_WORKFLOW, Blob: defaultWorkflowBlob() },
		],
		Bindings:
		{
			[paymentHash(tmpID)]: PAYMENT_WORKFLOW,
			[changeOrderHash(tmpID)]: CHANGEORDER_WORKFLOW,
		},
	};
}

module.exports = {
	buildTemplate,
	HEADLIGHT_IDCUSTOMER,
	MODULE_HASH,
	MODULE_NAME,
	MODULE_ORDINAL,
	MODULE_ACCESS_CAPABILITY,
	PAYMENT_WORKFLOW,
	CHANGEORDER_WORKFLOW,
	paymentHash,
	changeOrderHash,
	paymentGUID,
	changeOrderGUID,
};
